import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, Check } from 'lucide-react';

const SEGMENTS = [
  { id: 1, title: 'Base Camp' },
  { id: 2, title: 'Trail Markers' },
  { id: 3, title: 'The Briefing' },
  { id: 4, title: 'Gear Check' },
  { id: 5, title: 'Prompt School' },
  { id: 6, title: 'The Build' },
  { id: 7, title: 'The Exchange' },
  { id: 8, title: 'Show & Tell' },
  { id: 9, title: 'The Sustainable Stack' },
];

const StateCircle = ({ state }) => {
  if (state === 'completed')
    return (
      <span className="w-5 h-5 rounded-full bg-brand-teal flex items-center justify-center shrink-0">
        <Check size={10} className="text-white" strokeWidth={3} />
      </span>
    );
  if (state === 'current')
    return (
      <span className="w-5 h-5 rounded-full border-2 border-brand-orange flex items-center justify-center shrink-0">
        <span className="w-1.5 h-1.5 rounded-full bg-brand-orange" />
      </span>
    );
  if (state === 'visited')
    return <span className="w-5 h-5 rounded-full border-2 border-brand-orange/40 shrink-0" />;
  return <span className="w-5 h-5 rounded-full border-2 border-gray-200 shrink-0" />;
};

const SegmentNavigator = ({ currentId, isCompleted, isVisited }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const ref = useRef(null);
  const current = SEGMENTS.find((s) => s.id === currentId);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const getState = (seg) => {
    if (seg.id === currentId) return 'current';
    if (isCompleted(seg.id)) return 'completed';
    if (isVisited(seg.id)) return 'visited';
    return 'upcoming';
  };

  return (
    <div ref={ref} className="relative" data-testid="segment-navigator">
      {/* Trigger */}
      <button
        onClick={() => setOpen(!open)}
        data-testid="segment-navigator-trigger"
        className="flex items-center gap-2 pl-3 pr-4 py-2 rounded-full border border-black/15 bg-white hover:border-black/25 transition-colors text-sm shadow-sm"
      >
        <StateCircle state="current" />
        <span className="text-ink-muted text-xs font-medium">You are here</span>
        <span className="text-ink-muted text-xs">·</span>
        <span className="font-semibold text-ink truncate max-w-[220px]">
          {currentId} · {current?.title}
        </span>
        <ChevronDown
          size={14}
          className={`text-ink-secondary shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown */}
      {open && (
        <div
          data-testid="segment-navigator-dropdown"
          className="absolute top-full left-0 mt-2 w-80 bg-white rounded-2xl shadow-xl border border-black/[0.07] overflow-hidden z-50"
        >
          <div className="py-1.5">
            {SEGMENTS.map((seg) => {
              const state = getState(seg);
              return (
                <button
                  key={seg.id}
                  onClick={() => { navigate(`/session/${seg.id}`); setOpen(false); }}
                  data-testid={`navigator-item-${seg.id}`}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                    state === 'current' ? 'bg-orange-50 hover:bg-orange-100' : 'hover:bg-gray-50'
                  }`}
                >
                  <StateCircle state={state} />
                  <span
                    className={`text-sm flex-1 leading-snug ${
                      state === 'current'
                        ? 'font-bold text-ink'
                        : state === 'completed'
                        ? 'text-brand-teal font-medium'
                        : 'text-ink-secondary'
                    }`}
                  >
                    {seg.id} · {seg.title}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default SegmentNavigator;
